import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "../components/Brand";
import { useScreenInit } from "../useScreenInit";
import { authClient } from "../auth-client";
import { useSession } from "../session";
import { clearAdminTenant } from "../tenant-context";
import { safeReturnPath, tenantFromReturnPath } from "../lead-links";
import { PasswordReset } from "./PasswordReset";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const seed = useScreenInit();
  const [email, setEmail] = useState(seed.email !== undefined ? (seed.email as string) : "");
  const [password, setPassword] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState((seed.error as string) ?? "");
  const sessionExpired =
    searchParams.get("reason") === "session-expired" || seed.reason === "session-expired";
  const passwordResetFlow = searchParams.has("token") || searchParams.has("error");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Enter the email address linked to your account.");
      return;
    }
    if (!password) {
      setError("Enter your password.");
      return;
    }
    setWorking(true);
    setError("");
    try {
      const result = await authClient.signIn.email({ email: email.trim(), password });
      if (result.error) {
        setError("The email address or password is incorrect.");
        return;
      }

      const returnTo = safeReturnPath(searchParams.get("returnTo"), window.location.origin);
      const linkedTenant = tenantFromReturnPath(returnTo, window.location.origin);
      clearAdminTenant();
      const enteredLinkedTenant = linkedTenant
        ? await session.enterAdminTenant(linkedTenant)
        : false;
      const refreshed = enteredLinkedTenant || (await session.refresh());
      if (!refreshed) {
        setError("You were signed in, but your session could not be loaded. Please try again.");
        return;
      }

      navigate(returnTo ?? "/", {
        replace: true,
        state: { notice: "Signed in successfully." },
      });
    } catch {
      setError("We couldn't sign you in. Check your connection and try again.");
    } finally {
      setWorking(false);
    }
  };

  const inputCls =
    "w-full h-11 px-3.5 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/60 transition-colors";

  if (passwordResetFlow) return <PasswordReset />;

  return (
    <div className="min-h-screen bg-surface-light flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[400px]">
        <div className="flex justify-center mb-8">
          <Brand logoHeight={62} align="center" />
        </div>

        <div className="bg-surface-white border border-default rounded-xl px-[30px] py-[30px]">
          <h1 className="text-title-md font-bold text-primary mb-1">Sign in</h1>
          <p className="text-body text-secondary mb-6">
            Access leads captured and delivered for your organisation.
          </p>

          {sessionExpired && (
            <div
              role="status"
              className="mb-5 flex items-start gap-3 rounded-md border border-brand-red/20 bg-[rgba(225,0,42,0.06)] px-3.5 py-3"
            >
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-red/10 text-brand-red">
                <i className="fa-solid fa-clock-rotate-left text-[11px]" aria-hidden="true" />
              </span>
              <div>
                <p className="text-caption font-semibold text-primary">Your session has expired</p>
                <p className="mt-0.5 text-caption text-secondary">
                  For your security, you have been signed out. Sign in again to continue.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="client-email" className="text-caption font-medium text-primary">
                Email address
              </label>
              <input
                id="client-email"
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.co.uk"
                className={inputCls}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between">
                <label htmlFor="client-password" className="text-caption font-medium text-primary">
                  Password
                </label>
                <button
                  type="button"
                  onClick={async () => {
                    if (!email.trim()) {
                      setError("Enter your email address first.");
                      return;
                    }
                    const result = await authClient.requestPasswordReset({
                      email: email.trim(),
                      redirectTo: "/reset-password",
                    });
                    setError(
                      result.error
                        ? (result.error.message ?? "Reset request failed.")
                        : "If the account exists, a reset email has been sent.",
                    );
                  }}
                  className="text-caption text-brand-red hover:underline"
                >
                  Forgot?
                </button>
              </div>
              <input
                id="client-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={inputCls}
              />
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-xs bg-[rgba(255,101,80,0.1)] px-3 py-2.5"
              >
                <i className="fa-solid fa-circle-exclamation text-[13px] text-status-red mt-0.5" />
                <span className="text-caption text-status-red">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={working}
              aria-busy={working}
              className="h-11 w-full rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors mt-1"
            >
              {working ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <p className="text-caption text-tertiary text-center mt-6">
          Need access?{" "}
          <button className="text-brand-red hover:underline">
            Contact your FMC account manager
          </button>
        </p>
      </div>
    </div>
  );
}
